import { useState, useEffect, useCallback, useRef } from 'react';
import { QRCodeService } from '../utils/qrCodeService';
import { CONFIG, authHeaders, authFetch } from '../utils/config';

export const useQRCodes = (limit = 20) => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cachedStats, setCachedStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [globalStatsData, setGlobalStatsData] = useState(null);
  const [globalStatsLoading, setGlobalStatsLoading] = useState(false);
  const [qrCodeKinds, setQrCodeKinds] = useState({
    ai_qr_code_count: 0,
    classic_qr_code_count: 0,
    gs1_qr_code_count: 0
  });

  const statsLoadedRef = useRef(false);

  const loadQRCodeKinds = useCallback(async () => {
    try {
      const kindsData = await QRCodeService.getQRCodeKinds();
      setQrCodeKinds(kindsData);
      const total = (kindsData.ai_qr_code_count || 0) +
        (kindsData.classic_qr_code_count || 0) +
        (kindsData.gs1_qr_code_count || 0);
      setTotalItems(total);
    } catch (err) {
      console.error('Erreur chargement types QR codes:', err);
    }
  }, []);

  const mapStatsToQRCodes = useCallback((statsData, qrCodesList) => {
    if (!statsData?.perUrlStats || qrCodesList.length === 0) return;
    const newCachedStats = {};
    qrCodesList.forEach((qr) => {
      const shortUrl = qr.short_url;
      if (shortUrl && statsData.perUrlStats[shortUrl]) {
        newCachedStats[qr.uuid] = statsData.perUrlStats[shortUrl];
      }
    });
    if (Object.keys(newCachedStats).length > 0) {
      setCachedStats(prev => ({ ...prev, ...newCachedStats }));
    }
  }, []);

  const loadGlobalStats = useCallback(async (qrCodesList = []) => {
    try {
      setGlobalStatsLoading(true);
      const stats = await QRCodeService.getAllQRCodeStats();
      setGlobalStatsData(stats);
      statsLoadedRef.current = true;
      mapStatsToQRCodes(stats, qrCodesList);
      return stats;
    } catch (err) {
      console.error('Erreur chargement stats globales:', err);
      return null;
    } finally {
      setGlobalStatsLoading(false);
    }
  }, [mapStatsToQRCodes]);

  const loadQRCodes = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      setError(null);

      const [data, kindsData] = await Promise.all([
        QRCodeService.getAllQRCodes({
          limit,
          page: currentPage,
          sort_by: 'created_at',
          sort_order: 'desc',
          ...params
        }),
        QRCodeService.getQRCodeKinds()
      ]);

      const total =
        (kindsData.ai_qr_code_count || 0) +
        (kindsData.classic_qr_code_count || 0) +
        (kindsData.gs1_qr_code_count || 0);

      setQrCodeKinds(kindsData);
      setTotalItems(total);
      let qrCodesArray = [];
      let paginationInfo = {};
      if (Array.isArray(data)) {
        qrCodesArray = data;
      } else if (data && Array.isArray(data.qr_codes)) {
        qrCodesArray = data.qr_codes;
        paginationInfo = {
          page: data.page || currentPage,
          last_page: data.last_page || 1,
          total: data.total || qrCodesArray.length
        };
      } else if (data && Array.isArray(data.data)) {
        qrCodesArray = data.data;
      } else if (data && Array.isArray(data.qrCodes)) {
        qrCodesArray = data.qrCodes;
      } else if (data && Array.isArray(data.items)) {
        qrCodesArray = data.items;
      } else {
        console.warn('Format de données inattendu:', data);
        qrCodesArray = [];
      }

      if (globalStatsData) {
        mapStatsToQRCodes(globalStatsData, qrCodesArray);
      }

      const correctedPages = total <= limit ? 1 : paginationInfo.last_page;
      setTotalPages(correctedPages);
      setQrCodes(qrCodesArray);

    } catch (err) {
      setError(err.message || 'Erreur lors du chargement des QR codes');
      console.error('Erreur chargement QR codes:', err);
      setQrCodes([]);
    } finally {
      setLoading(false);
    }
  }, [limit, currentPage, globalStatsData, mapStatsToQRCodes]);

  const updateQRCode = useCallback((updatedQRCode) => {
    setQrCodes(prev =>
      prev.map(qr => {
        if (qr.uuid === updatedQRCode.uuid) {
          return {
            ...qr,
            ...updatedQRCode,
            item_type: updatedQRCode.item_type || qr.item_type,
            created_at: updatedQRCode.created_at || qr.created_at,
            mignature: updatedQRCode.mignature || qr.mignature
          };
        }
        return qr;
      })
    );
  }, []);

  const deleteQRCode = useCallback(async (uuid) => {
    try {
      const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/${uuid}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (!response.ok) {
        throw new Error(`Erreur lors de la suppression (${response.status})`);
      }

      setQrCodes(prev => {
        const newQrCodes = prev.filter(qr => qr.uuid !== uuid);

        if (newQrCodes.length === 0 && currentPage > 1) {
          setCurrentPage(currentPage - 1);
          loadQRCodes();
        }

        return newQrCodes;
      });

      setCachedStats(prev => {
        const newStats = { ...prev };
        delete newStats[uuid];
        return newStats;
      });
    } catch (error) {
      console.error('Erreur suppression QR code:', error);
      throw error;
    }
  }, [currentPage, loadQRCodes]);

  const cacheStats = useCallback((uuid, stats) => {
    setCachedStats(prev => ({
      ...prev,
      [uuid]: stats
    }));
  }, []);

  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [currentPage, totalPages]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const getGlobalStats = useCallback(() => {
    const qrCodesArray = Array.isArray(qrCodes) ? qrCodes : [];

    const totalQRCodes = qrCodeKinds.ai_qr_code_count + qrCodeKinds.classic_qr_code_count + qrCodeKinds.gs1_qr_code_count;
    const activeQRCodes = qrCodesArray.filter(qr => !qr.expires_at || new Date(qr.expires_at) > new Date()).length;

    const totalScans = globalStatsData?.total_visits || 0;
    const weeklyScans = globalStatsData?.visits_this_week || 0;

    return {
      totalQRCodes,
      activeQRCodes,
      totalScans,
      weeklyScans,
      aiQRCodes: qrCodeKinds.ai_qr_code_count,
      classicQRCodes: qrCodeKinds.classic_qr_code_count,
      gs1QRCodes: qrCodeKinds.gs1_qr_code_count
    };
  }, [qrCodes, globalStatsData, qrCodeKinds]);

  useEffect(() => {
    if (!statsLoadedRef.current) {
      loadGlobalStats();
    }
  }, []);

  useEffect(() => {
    loadQRCodes();
  }, [loadQRCodes]);

  return {
    qrCodes,
    loading,
    error,
    cachedStats,
    currentPage,
    totalPages,
    totalItems,
    qrCodeKinds,
    globalStatsLoading,
    loadQRCodes,
    loadQRCodeKinds,
    loadGlobalStats,
    updateQRCode,
    deleteQRCode,
    cacheStats,
    getGlobalStats,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    refetch: loadQRCodes
  };
};
